import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Package, TrendingUp, Calendar as CalendarIcon, Filter, Download, Trash2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { format, parseISO, subDays } from 'date-fns';
import LogProductionModal from './LogProductionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAN_LOG_ROLES = ['grower', 'growing_supervisor', 'owner'];

const gradeBadgeClass = (grade) => {
  switch (grade) {
    case 'A':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'B':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'C':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

const todayISO = () => format(new Date(), 'yyyy-MM-dd');

const ActualProductionView = ({ userRole = 'owner', currentUserId = null }) => {
  const [records, setRecords] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [activeCycles, setActiveCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [filterRoom, setFilterRoom] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const canLog = CAN_LOG_ROLES.includes(userRole);
  const canDeleteAny = userRole === 'owner';
  const canDeleteOwn = userRole === 'grower' || userRole === 'growing_supervisor';

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filterRoom) params.room_id = filterRoom;
      if (filterStart) params.start_date = filterStart;
      if (filterEnd) params.end_date = filterEnd;

      const [prodRes, roomsRes, cyclesRes] = await Promise.all([
        axios.get(`${API}/production`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        }),
        axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/room-cycles?status=active`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setRecords(prodRes.data.records || []);
      setRooms(roomsRes.data || []);
      const cyclesData = Array.isArray(cyclesRes.data)
        ? cyclesRes.data
        : (cyclesRes.data.cycles || []);
      setActiveCycles(cyclesData);
    } catch (error) {
      toast.error('Failed to load production records');
    } finally {
      setLoading(false);
    }
  }, [filterRoom, filterStart, filterEnd]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = useMemo(() => {
    const today = todayISO();
    const weekStart = format(subDays(new Date(), 6), 'yyyy-MM-dd');
    const monthStart = format(subDays(new Date(), 29), 'yyyy-MM-dd');
    let todayKg = 0;
    let weekKg = 0;
    let monthKg = 0;
    let totalKg = 0;
    records.forEach((r) => {
      const d = (r.harvest_date || '').slice(0, 10);
      const w = r.weight_kg || 0;
      totalKg += w;
      if (d === today) todayKg += w;
      if (d >= weekStart) weekKg += w;
      if (d >= monthStart) monthKg += w;
    });
    return { todayKg, weekKg, monthKg, totalKg, count: records.length };
  }, [records]);

  const clearFilters = () => {
    setFilterRoom('');
    setFilterStart('');
    setFilterEnd('');
  };

  const handleDelete = async (record) => {
    const label = `${record.weight_kg ?? 0}kg from ${record.room_name || 'room'} on ${(record.harvest_date || '').slice(0, 10)}`;
    if (!window.confirm(`Delete this production record?\n\n${label}\n\nThis cannot be undone.`)) {
      return;
    }
    setDeletingId(record.id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/production/${record.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Production record deleted');
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to delete production record';
      toast.error(detail);
    } finally {
      setDeletingId(null);
    }
  };

  const canDeleteRecord = (record) => {
    if (canDeleteAny) return true;
    if (canDeleteOwn && currentUserId && record.logged_by === currentUserId) return true;
    return false;
  };
  // Owner can edit any record; grower / growing supervisor can edit their own.
  const canEditRecord = (record) => canDeleteRecord(record);
  const showActionsColumn = canDeleteAny || canDeleteOwn;

  const exportCsv = () => {
    if (records.length === 0) {
      toast.info('Nothing to export');
      return;
    }
    const header = ['Date', 'Room', 'Batch', 'Quantity (kg)', 'Grade', 'Logged By', 'Notes'];
    const rows = records.map((r) => [
      (r.harvest_date || '').slice(0, 10),
      r.room_name || '',
      r.batch_name || '',
      r.weight_kg ?? '',
      r.quality_grade || '',
      r.logged_by_name || '',
      (r.notes || '').replace(/"/g, '""')
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `actual-production-${todayISO()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="actual-production-view">
      {/* Header + Action */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Actual Production</h2>
          <p className="text-sm text-gray-500">Harvest records logged by your team</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            data-testid="export-production-csv"
          >
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
          {canLog && (
            <Button
              size="sm"
              onClick={() => setShowLogModal(true)}
              className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
              data-testid="open-log-production-modal"
            >
              <Plus className="w-4 h-4 mr-1" />
              Log Production
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="actual-production-stats">
        <Card className="border-gray-100 shadow-sm">
          <CardHeader className="pb-2 p-3 md:p-4">
            <CardTitle className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <Package className="w-3 h-3" />
              Today
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 md:p-4 pt-0">
            <div className="text-2xl font-bold text-gray-900">{stats.todayKg.toFixed(1)} kg</div>
          </CardContent>
        </Card>
        <Card className="border-gray-100 shadow-sm">
          <CardHeader className="pb-2 p-3 md:p-4">
            <CardTitle className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              Last 7 Days
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 md:p-4 pt-0">
            <div className="text-2xl font-bold text-gray-900">{stats.weekKg.toFixed(1)} kg</div>
          </CardContent>
        </Card>
        <Card className="border-gray-100 shadow-sm">
          <CardHeader className="pb-2 p-3 md:p-4">
            <CardTitle className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <CalendarIcon className="w-3 h-3" />
              Last 30 Days
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 md:p-4 pt-0">
            <div className="text-2xl font-bold text-gray-900">{stats.monthKg.toFixed(1)} kg</div>
          </CardContent>
        </Card>
        <Card className="border-gray-100 shadow-sm bg-[#F2F8F4]">
          <CardHeader className="pb-2 p-3 md:p-4">
            <CardTitle className="text-xs font-medium text-[#1A4D2E] flex items-center gap-1">
              <Package className="w-3 h-3" />
              Total Entries
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 md:p-4 pt-0">
            <div className="text-2xl font-bold text-[#1A4D2E]">{stats.count}</div>
            <p className="text-xs text-[#1A4D2E]/70 mt-1">{stats.totalKg.toFixed(1)} kg total</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-gray-100">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Room</Label>
              <select
                value={filterRoom}
                onChange={(e) => setFilterRoom(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                data-testid="filter-room-select"
              >
                <option value="">All rooms</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={filterStart}
                onChange={(e) => setFilterStart(e.target.value)}
                className="mt-1"
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={filterEnd}
                onChange={(e) => setFilterEnd(e.target.value)}
                className="mt-1"
                data-testid="filter-end-date"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="w-full"
                data-testid="clear-filters-btn"
              >
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card>
        <CardHeader>
          <CardTitle>Production Records ({records.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-gray-500">Loading...</p>
          ) : records.length === 0 ? (
            <p className="text-center py-8 text-gray-500" data-testid="no-records">
              No production records found.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="production-records-table">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Room</th>
                    <th className="px-3 py-2 text-left">Batch</th>
                    <th className="px-3 py-2 text-right">Quantity (kg)</th>
                    <th className="px-3 py-2 text-center">Grade</th>
                    <th className="px-3 py-2 text-left hidden md:table-cell">Logged By</th>
                    <th className="px-3 py-2 text-left hidden lg:table-cell">Notes</th>
                    {showActionsColumn && <th className="px-3 py-2 text-center w-20">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {records.map((r, idx) => (
                    <tr key={r.id || idx} className="hover:bg-gray-50" data-testid={`production-row-${idx}`}>
                      <td className="px-3 py-2 text-gray-900">
                        {r.harvest_date ? format(parseISO(r.harvest_date), 'dd MMM yyyy') : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-900 font-medium">{r.room_name || '—'}</td>
                      <td className="px-3 py-2 text-gray-700">{r.batch_name || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">
                        {(r.weight_kg ?? 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={gradeBadgeClass(r.quality_grade)} variant="outline">
                          {r.quality_grade || 'A'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-gray-600 hidden md:table-cell">
                        {r.logged_by_name || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-500 hidden lg:table-cell truncate max-w-[200px]">
                        {r.notes || '—'}
                      </td>
                      {showActionsColumn && (
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {canEditRecord(r) && (
                              <button
                                type="button"
                                data-testid={`edit-production-${idx}`}
                                onClick={() => setEditRecord(r)}
                                className="inline-flex items-center justify-center p-1.5 rounded-md text-blue-600 hover:bg-blue-50"
                                title="Edit this production record"
                                aria-label="Edit production record"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            {canDeleteRecord(r) ? (
                              <button
                                type="button"
                                data-testid={`delete-production-${idx}`}
                                onClick={() => handleDelete(r)}
                                disabled={deletingId === r.id}
                                className="inline-flex items-center justify-center p-1.5 rounded-md text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                                title="Delete this production record"
                                aria-label="Delete production record"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            ) : (
                              !canEditRecord(r) && <span className="text-gray-300 text-xs">—</span>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {showLogModal && (
        <LogProductionModal
          rooms={rooms}
          activeCycles={activeCycles}
          harvestOnly={userRole !== 'owner'}
          onClose={() => setShowLogModal(false)}
          onSuccess={() => {
            setShowLogModal(false);
            fetchData();
          }}
        />
      )}

      {editRecord && (
        <LogProductionModal
          rooms={rooms}
          activeCycles={activeCycles}
          editRecord={editRecord}
          onClose={() => setEditRecord(null)}
          onSuccess={() => {
            setEditRecord(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default ActualProductionView;
